import React, { useState, useMemo, useEffect, useCallback } from 'react';

import { Link } from 'react-router-dom';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import moment from 'moment';
import isEmpty from 'lodash/isEmpty';
import { IMyTodo, IMyTodoUserRef, IMyTodoListRow } from './store/my-todo.types';
import { resolveLocationDisplay, resolveLocationRoutePath } from './my-todo-location';
import Loader from '../../../../Components/Utility/Loader/Loader';
import CustomTooltip from '../../../../Components/Tooltip/CustomTooltip';
import { getImageFullUrlBasedonPath, shortenStringWithTooltip, shortenandHoverString } from '../../../../Components/helper';
import './MyTodo.css';
import { useProductLandingPath } from '../../../../Components/CustomHooks/useProductLandingPath';

const ConfirmationPopup = require('../../../../Components/ModalPopup/ConfirmationPopup').default as React.FC<{
  onOpen: boolean;
  onConfirm: () => void;
  title: string;
  message: string;
  toggle: () => void;
}>;

const noImage = require('../../../../assets/images/no-image.png') as string;

const SCROLLABLE_DIV_ID = 'scrollableDiv_My_Todo';

const resolveLocationUrl = (todo: IMyTodo): string => {
  const devId = getDevOrderId(todo);
  if (!devId) return '#';
  return `/development-order/${devId}/${resolveLocationRoutePath(todo.location)}`;
};

const resolveTodoImageSrc = (path?: string | null): string => {
  if (!path) return noImage;
  if (path.startsWith('https://') || path.startsWith('http://')) return path;
  return getImageFullUrlBasedonPath(path);
};

const formatMyTodoUserDisplay = (user: IMyTodoUserRef | undefined): string => {
  if (!user) return '-';
  const name = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
  return name || '-';
};

const getDevOrderId = (todo: IMyTodo): string | undefined => {
  const {devOrderId} = todo;
  if (typeof devOrderId === 'string') return devOrderId;
  return devOrderId && typeof devOrderId === 'object' ? (devOrderId as { _id?: string })._id : undefined;
};

const getDevOrderImage = (todo: IMyTodo): string | undefined =>
  typeof todo.devOrderId === 'object' && todo.devOrderId ? (todo.devOrderId as { image?: string }).image : undefined;

const getDevOrderNumber = (todo: IMyTodo): string | undefined =>
  typeof todo.devOrderId === 'object' && todo.devOrderId ? (todo.devOrderId as { number?: string }).number : undefined;

const getGroupDevOrderId = (row: IMyTodoListRow): string | undefined => {
  const {devOrderId} = row;
  if (devOrderId && typeof devOrderId === 'object' && devOrderId._id) return devOrderId._id;
  return undefined;
};

const getGroupDevOrderImage = (row: IMyTodoListRow): string | undefined => row.devOrderId?.image;

const getGroupDevOrderNumber = (row: IMyTodoListRow): string | undefined => row.devOrderId?.number;

const getLeafTasks = (row: IMyTodoListRow): IMyTodo[] => {
  if (row.hasExpandData) {
    return row.expandData ?? [];
  }
  if (row.task) {
    return [row.task];
  }
  const only = row.expandData?.length === 1 ? row.expandData[0] : undefined;
  return only ? [only] : [];
};

type AllTodoExpandSegment = { kind: 'flat'; task: IMyTodo } | { kind: 'assigneeNest'; assigneeKey: string; tasks: IMyTodo[] };

function buildAllTodoExpandSegments(leafTasks: IMyTodo[]): AllTodoExpandSegment[] {
  const order: string[] = [];
  const byAssignee = new Map<string, IMyTodo[]>();
  for (const t of leafTasks) {
    const k = t.assignedTo?._id ?? 'unassigned';
    if (!byAssignee.has(k)) {
      order.push(k);
      byAssignee.set(k, []);
    }
    byAssignee.get(k)!.push(t);
  }
  const out: AllTodoExpandSegment[] = [];
  for (const k of order) {
    const arr = byAssignee.get(k)!;
    const locKeys = new Set(arr.map((x) => (x.location ?? '').trim()));
    const multiModule = arr.length > 1 && locKeys.size > 1;
    if (multiModule) {
      out.push({ kind: 'assigneeNest', assigneeKey: k, tasks: arr });
    } else {
      for (const task of arr) {
        out.push({ kind: 'flat', task });
      }
    }
  }
  return out;
}

const segmentsCache = new WeakMap<IMyTodo[], AllTodoExpandSegment[]>();

function getAllTodoExpandSegments(leafTasks: IMyTodo[]): AllTodoExpandSegment[] {
  let cached = segmentsCache.get(leafTasks);
  if (!cached) {
    cached = buildAllTodoExpandSegments(leafTasks);
    segmentsCache.set(leafTasks, cached);
  }
  return cached;
}

function nestCompletionPercent(tasks: IMyTodo[]): number {
  const total = tasks.length;
  if (total <= 0) {
    return 0;
  }
  const done = tasks.filter((x) => x.status === 'Done').length;
  return Math.round((done / total) * 10000) / 100;
}

function mergeTaskWithDevOrder(fallback: IMyTodoListRow['devOrderId'], todo: IMyTodo): IMyTodo {
  const {devOrderId} = todo;
  const hasDev = devOrderId && (typeof devOrderId === 'string' ? devOrderId.length > 0 : Boolean((devOrderId as { _id?: string })._id));
  if (hasDev || !fallback) {
    return todo;
  }
  return { ...todo, devOrderId: fallback };
}

const formatDateOnly = (dateString: string) => moment(dateString).format('DD-MMMM-YYYY');
const formatTimeOnly = (dateString: string) => moment(dateString).format('HH:mm');

const distinctAssignedByLabel = (tasks: IMyTodo[], multipleLabel: string) => {
  const ids = new Set(tasks.map((x) => x.assignedBy?._id).filter(Boolean));
  if (ids.size <= 1) return formatMyTodoUserDisplay(tasks[0]?.assignedBy);
  return multipleLabel;
};

const distinctAssignedToLabel = (tasks: IMyTodo[], multipleLabel: string) => {
  const ids = new Set(tasks.map((x) => x.assignedTo?._id).filter(Boolean));
  if (ids.size <= 1) return formatMyTodoUserDisplay(tasks[0]?.assignedTo);
  return multipleLabel;
};

const ProductThumb: React.FC<{ src: string; alt?: string }> = ({ src, alt = 'Product' }) => {
  const [errored, setErrored] = useState(false);
  useEffect(() => {
    setErrored(false);
  }, [src]);
  return <img src={errored || !src ? noImage : src} alt={alt} loading="lazy" onError={() => setErrored(true)} />;
};

const CompletionProgress: React.FC<{ percent: number }> = ({ percent }) => {
  const clampedPercent = Math.min(100, Math.max(0, percent));
  const isComplete = clampedPercent === 100;
  return (
    <div className={`my-todo-completion-progress${isComplete ? ' my-todo-completion-progress--complete' : ''}`}>
      <div className="my-todo-completion-progress__track">
        <div className="my-todo-completion-progress__fill" style={{ width: `${clampedPercent}%`, backgroundColor: `${isComplete ? '#70c2ac' : '#FDD499'}` }} />
      </div>
      <span className="my-todo-completion-progress__label">{Math.round(percent)}%</span>
    </div>
  );
};

interface TaskRowProps {
  todo: IMyTodo;
  fallbackDevOrder?: IMyTodoListRow['devOrderId'];
  indent: 'none' | 'task' | 'task-nested';
  isSelected: boolean;
  showAssigneeColumn: boolean;
  viewType: 'my-todo' | 'all-todo';
  onCheckboxChange: (todoId: string, checked: boolean) => void;
  onMarkDone: (todoId: string) => void;
  onDelete: (todoId: string) => void;
}

const TaskRow = React.memo(function TaskRow({
  todo: rawTodo,
  fallbackDevOrder,
  indent,
  isSelected,
  showAssigneeColumn,
  viewType,
  onCheckboxChange,
  onMarkDone,
  onDelete,
}: TaskRowProps) {
  const { t } = useTranslation('modules/my-todo');
  const todo = mergeTaskWithDevOrder(fallbackDevOrder ?? null, rawTodo);
  const indentClass = indent === 'task-nested' ? 'my-todo-row--task-nested' : indent === 'task' ? 'my-todo-row--task' : '';
  const doneClass = todo.status === 'Done' ? 'my-todo-row--done' : '';
  return (
    <tr className={[indentClass, doneClass].filter(Boolean).join(' ')}>
      <td className="w-50px">
        <div className="common-checkbox margin-auto">
          <input type="checkbox" id={`todo-${todo._id}`} checked={isSelected} onChange={(e) => onCheckboxChange(todo._id, e.target.checked)} />
          <label htmlFor={`todo-${todo._id}`} className="chklabel" />
        </div>
      </td>
      <td className="text-center">
        <span className="my-todo-expand-placeholder" aria-hidden="true" />
      </td>
      <td className="text-center image-detail-my-todo">
        <div className="my-todo-product-thumb">
          <ProductThumb src={resolveTodoImageSrc(getDevOrderImage(todo))} alt={getDevOrderNumber(todo) ?? t('MY_TODO.LIST.PRODUCT')} />
        </div>
      </td>
      <td>
        {getDevOrderId(todo) ? (
          <Link to={resolveLocationUrl(todo)} className="text-black text-medium text-decoration-underline" target="_blank" rel="noopener noreferrer">
            {shortenStringWithTooltip(getDevOrderNumber(todo) ?? '-', 30)}
          </Link>
        ) : (
          <span className="text-muted">{shortenStringWithTooltip(getDevOrderNumber(todo) ?? '-', 30)}</span>
        )}
      </td>
      <td className="task-desc">{shortenandHoverString(todo.description ?? '-', 40)}</td>
      <td>
        <span className="text-black">{shortenStringWithTooltip(formatMyTodoUserDisplay(todo.assignedBy), 20)}</span>
      </td>
      {showAssigneeColumn && (
        <td>
          <span className="text-black">{shortenStringWithTooltip(formatMyTodoUserDisplay(todo.assignedTo), 20)}</span>
        </td>
      )}
      <td className="text-center text-medium align-middle my-todo-completion-cell">
        <span className={`my-todo-completion-label ${todo.status === 'Done' ? 'my-todo-completion-label--done' : 'my-todo-completion-label--pending'}`}>
          {todo.status === 'Done' ? t('MY_TODO.STATUS.DONE') : t('MY_TODO.STATUS.PENDING')}
        </span>
      </td>
      <td>
        <span className="text-bold">{shortenStringWithTooltip(resolveLocationDisplay(todo.location), 30)}</span>
      </td>
      <td className="text-nowrap">{formatDateOnly(todo.assignedAt)}</td>
      <td className="text-nowrap">{formatTimeOnly(todo.assignedAt)}</td>
      <td className="w-100px">
        <div className="d-flex align-items-center justify-content-end">
          {viewType === 'my-todo' && todo.status === 'Pending' && (
            <CustomTooltip placement="top" tooltipText={t('MY_TODO.LIST.MARK_AS_DONE')}>
              <button type="button" className="btn d-flex p-0 mr-8" onClick={() => onMarkDone(todo._id)} aria-label={t('MY_TODO.LIST.MARK_AS_DONE')}>
                <i className="icon-Complete-task f-20" />
              </button>
            </CustomTooltip>
          )}
          <button type="button" className="btn d-flex p-0" aria-label={t('MY_TODO.LIST.VIEW_DETAILS')}>
            <OverlayTrigger
              placement="bottom"
              trigger="hover"
              rootClose
              overlay={
                <Popover id={`info-${todo._id}`} className="custom-popover modified-popover">
                  <Popover.Content>
                    <div>
                      <p className="text-left m-0">
                        <strong>{t('MY_TODO.LIST.CREATED')}</strong>: {formatMyTodoUserDisplay(todo.assignedBy)}{' '}
                        {todo.assignedAt && moment(todo.assignedAt).format('DD-MMM-YYYY, HH:mm')}
                      </p>
                      {todo.updatedAt && todo.updatedAt !== todo.assignedAt && (
                        <p className="text-left m-0">
                          <strong>{t('MY_TODO.LIST.MODIFIED')}</strong>: {formatMyTodoUserDisplay(todo.assignedBy)}{' '}
                          {moment(todo.updatedAt).format('DD-MMM-YYYY, HH:mm')}
                        </p>
                      )}
                    </div>
                  </Popover.Content>
                </Popover>
              }
            >
              <i className="icon icon-Information f-16" />
            </OverlayTrigger>
          </button>
          {viewType === 'my-todo' && (
            <CustomTooltip placement="top" tooltipText={todo.status === 'Pending' ? t('MY_TODO.LIST.DELETE_DISABLED_PENDING') : t('MY_TODO.LIST.DELETE_TASK')}>
              <button
                type="button"
                className={`btn d-flex ${todo.status === 'Pending' ? 'btn-pending-mytodo' : ' btn-delete-mytodo'} p-0 ml-8`}
                disabled={todo.status === 'Pending'}
                onClick={() => {
                  if (todo.status === 'Done') onDelete(todo._id);
                }}
                aria-label={t('MY_TODO.LIST.DELETE_TASK')}
              >
                <i className={`icon-Delete f-16${todo.status === 'Pending' ? ' text-muted' : ''}`} />
              </button>
            </CustomTooltip>
          )}
        </div>
      </td>
    </tr>
  );
});

interface AssigneeNestRowProps {
  parentRow: IMyTodoListRow;
  nestTasks: IMyTodo[];
  nestKey: string;
  expanded: boolean;
  selected: boolean;
  showAssigneeColumn: boolean;
  onToggleExpand: (nestKey: string) => void;
  onToggleSelect: (nestTasks: IMyTodo[], checked: boolean) => void;
}

const AssigneeNestRow = React.memo(function AssigneeNestRow({
  parentRow,
  nestTasks,
  nestKey,
  expanded,
  selected,
  showAssigneeColumn,
  onToggleExpand,
  onToggleSelect,
}: AssigneeNestRowProps) {
  const { t } = useTranslation('modules/my-todo');
  const productLandingPath = useProductLandingPath();
  const thumbSrc = resolveTodoImageSrc(getGroupDevOrderImage(parentRow) ?? (nestTasks[0] ? getDevOrderImage(nestTasks[0]) : undefined));
  const pct = nestCompletionPercent(nestTasks);
  const trClass = ['my-todo-row--assignee-nest'];
  if (expanded) {
    trClass.push('my-todo-expanded-row');
  }
  return (
    <tr className={trClass.join(' ')}>
      <td className="w-50px">
        <div className="common-checkbox margin-auto">
          <input type="checkbox" id={`nest-chk-${nestKey}`} checked={selected} onChange={(e) => onToggleSelect(nestTasks, e.target.checked)} />
          <label htmlFor={`nest-chk-${nestKey}`} className="chklabel" />
        </div>
      </td>
      <td className="text-center">
        <button
          type="button"
          className="btn-up-down"
          aria-expanded={expanded}
          aria-label={expanded ? t('MY_TODO.LIST.COLLAPSE_ALL') : t('MY_TODO.LIST.EXPAND_ALL')}
          onClick={() => onToggleExpand(nestKey)}
        >
          <span className={`icon-new-down-arrow-icon ${expanded ? 'icon-inverse' : ''}`} />
        </button>
      </td>
      <td className="text-center image-detail-my-todo">
        <div className="my-todo-product-thumb">
          <ProductThumb src={thumbSrc} alt={getGroupDevOrderNumber(parentRow) ?? t('MY_TODO.LIST.PRODUCT')} />
        </div>
      </td>
      <td>
        {getGroupDevOrderId(parentRow) ? (
          <Link
            to={productLandingPath(getGroupDevOrderId(parentRow) ?? '')}
            className="text-black text-medium text-decoration-underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            {getGroupDevOrderNumber(parentRow)}
          </Link>
        ) : (
          <span className="text-muted">{getGroupDevOrderNumber(parentRow) ?? '-'}</span>
        )}
      </td>
      <td>
        <span className="text-truncate task-desc d-inline-block my-todo-desc-cell">{t('MY_TODO.LIST.MULTIPLE')}</span>
      </td>
      <td>
        <span className="text-black">{shortenStringWithTooltip(formatMyTodoUserDisplay(nestTasks[0]?.assignedBy), 20)}</span>
      </td>
      {showAssigneeColumn && (
        <td>
          <span className="text-black">{shortenStringWithTooltip(formatMyTodoUserDisplay(nestTasks[0]?.assignedTo), 20)}</span>
        </td>
      )}
      <td className="text-center">
        <CompletionProgress percent={pct} />
      </td>
      <td>
        <span className="text-bold">{t('MY_TODO.LIST.MULTIPLE')}</span>
      </td>
      <td className="text-nowrap">{t('MY_TODO.LIST.MULTIPLE')}</td>
      <td className="text-nowrap">{t('MY_TODO.LIST.MULTIPLE')}</td>
      <td className="w-100px">
        <div className="d-flex justify-content-end">
          <button type="button" className="btn p-0" aria-label={t('MY_TODO.LIST.VIEW_DETAILS')}>
            <OverlayTrigger
              placement="bottom"
              trigger="hover"
              rootClose
              overlay={
                <Popover id={`nest-info-${nestKey}`} className="custom-popover modified-popover">
                  <Popover.Content>
                    <div>
                      <p className="text-left m-0">
                        <strong>{t('MY_TODO.LIST.CREATED')}</strong>: {formatMyTodoUserDisplay(nestTasks[0]?.assignedBy)}{' '}
                        {nestTasks[0]?.assignedAt && moment(nestTasks[0].assignedAt).format('DD-MMM-YYYY, HH:mm')}
                      </p>
                    </div>
                  </Popover.Content>
                </Popover>
              }
            >
              <i className="icon icon-Information f-16" />
            </OverlayTrigger>
          </button>
        </div>
      </td>
    </tr>
  );
});

interface GroupSummaryRowProps {
  row: IMyTodoListRow;
  expanded: boolean;
  selected: boolean;
  showAssigneeColumn: boolean;
  viewType: 'my-todo' | 'all-todo';
  onExpandToggle: (groupKey: string) => void;
  onToggleSelect: (row: IMyTodoListRow, checked: boolean) => void;
  onDeleteGroup: (ids: string[]) => void;
}

const GroupSummaryRow = React.memo(function GroupSummaryRow({
  row,
  expanded,
  selected,
  showAssigneeColumn,
  viewType,
  onExpandToggle,
  onToggleSelect,
  onDeleteGroup,
}: GroupSummaryRowProps) {
  const { t } = useTranslation('modules/my-todo');
  const productLandingPath = useProductLandingPath();
  const leafTasks = useMemo(() => getLeafTasks(row), [row]);
  const thumbSrc = resolveTodoImageSrc(getGroupDevOrderImage(row) ?? (leafTasks[0] ? getDevOrderImage(leafTasks[0]) : undefined));
  const allDone = row.completionPercent === 100 && leafTasks.length > 0;
  const trClass = ['my-todo-row--group'];
  if (row.hasExpandData && expanded) {
    trClass.push('my-todo-expanded-row');
  }
  return (
    <tr className={trClass.join(' ')}>
      <td className="w-50px">
        <div className="common-checkbox margin-auto">
          <input type="checkbox" id={`group-${row.groupKey}`} checked={selected} onChange={(e) => onToggleSelect(row, e.target.checked)} />
          <label htmlFor={`group-${row.groupKey}`} className="chklabel" />
        </div>
      </td>
      <td className="text-center">
        {row.hasExpandData ? (
          <button
            type="button"
            className="btn-up-down"
            aria-expanded={expanded}
            aria-label={expanded ? t('MY_TODO.LIST.COLLAPSE_ALL') : t('MY_TODO.LIST.EXPAND_ALL')}
            onClick={() => onExpandToggle(row.groupKey)}
          >
            <span className={`icon-new-down-arrow-icon ${expanded ? 'icon-inverse' : ''}`} />
          </button>
        ) : (
          <span className="my-todo-expand-placeholder" aria-hidden="true" />
        )}
      </td>
      <td className="text-center image-detail-my-todo">
        <div className="my-todo-product-thumb">
          <ProductThumb src={thumbSrc} alt={getGroupDevOrderNumber(row) ?? t('MY_TODO.LIST.PRODUCT')} />
        </div>
      </td>
      <td>
        {getGroupDevOrderId(row) ? (
          <Link
            to={productLandingPath(getGroupDevOrderId(row) ?? '')}
            className="text-black text-medium text-decoration-underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            {getGroupDevOrderNumber(row)}
          </Link>
        ) : (
          <span className="text-muted">{getGroupDevOrderNumber(row) ?? '-'}</span>
        )}
      </td>
      <td>
        <span className="task-desc d-inline-block my-todo-desc-cell">{t('MY_TODO.LIST.MULTIPLE')}</span>
      </td>
      <td>
        <span className="text-black">{shortenStringWithTooltip(distinctAssignedByLabel(leafTasks, t('MY_TODO.LIST.MULTIPLE')), 20)}</span>
      </td>
      {showAssigneeColumn && (
        <td>
          <span className="text-black">{shortenStringWithTooltip(distinctAssignedToLabel(leafTasks, t('MY_TODO.LIST.MULTIPLE')), 20)}</span>
        </td>
      )}
      <td className="text-center">
        <CompletionProgress percent={row.completionPercent} />
      </td>
      <td>
        <span className="text-bold">{t('MY_TODO.LIST.MULTIPLE')}</span>
      </td>
      <td className="text-nowrap">{t('MY_TODO.LIST.MULTIPLE')}</td>
      <td className="text-nowrap">{t('MY_TODO.LIST.MULTIPLE')}</td>
      <td className="w-100px ">
        <div className="d-flex justify-content-end">
          <button type="button" className="btn p-0 " aria-label={t('MY_TODO.LIST.VIEW_DETAILS')}>
            <OverlayTrigger
              placement="bottom"
              trigger="hover"
              rootClose
              overlay={
                <Popover id={`info-${row.groupKey}`} className="custom-popover modified-popover">
                  <Popover.Content>
                    <div>
                      <p className="text-left m-0">
                        <strong>{t('MY_TODO.LIST.CREATED')}</strong>: {formatMyTodoUserDisplay(leafTasks[0]?.assignedBy)}{' '}
                        {leafTasks[0]?.assignedAt && moment(leafTasks[0].assignedAt).format('DD-MMM-YYYY, HH:mm')}
                      </p>
                    </div>
                  </Popover.Content>
                </Popover>
              }
            >
              <i className="icon icon-Information f-16" />
            </OverlayTrigger>
          </button>
          {viewType === 'my-todo' && (
            <CustomTooltip placement="top" tooltipText={allDone ? t('MY_TODO.LIST.DELETE_TASK') : t('MY_TODO.LIST.DELETE_DISABLED_PENDING')}>
              <span>
                <button
                  type="button"
                  className={`btn d-flrc ${allDone ? ' btn-delete-mytodo' : 'btn-pending-mytodo'} p-0 ml-8`}
                  disabled={!allDone}
                  onClick={() => {
                    if (allDone) onDeleteGroup(leafTasks.map((x) => x._id));
                  }}
                  aria-label={t('MY_TODO.LIST.DELETE_TASK')}
                >
                  <i className={`icon-Delete f-16${!allDone ? ' text-muted' : ''}`} />
                </button>
              </span>
            </CustomTooltip>
          )}
        </div>
      </td>
    </tr>
  );
});

interface MyTodoListProps {
  groups: IMyTodoListRow[];
  loading: boolean;
  totalCount: number;
  currentPage: number;
  pageSize: number;
  onStatusUpdate: (id: string, status: 'Pending' | 'Done') => void;
  onBulkStatusUpdate: (ids: string[], status: 'Pending' | 'Done') => void;
  onDelete: (params: { id?: string; ids?: string[] }) => void;
  onLoadMore: () => void;
  showAssigneeColumn: boolean;
  viewType: 'my-todo' | 'all-todo';
}

const MyTodoList: React.FC<MyTodoListProps> = ({
  groups,
  loading,
  totalCount,
  currentPage,
  pageSize,
  onStatusUpdate,
  onBulkStatusUpdate,
  onDelete,
  onLoadMore,
  showAssigneeColumn,
  viewType,
}) => {
  const { t } = useTranslation('modules/my-todo');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [expandAll, setExpandAll] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [expandedAssigneeNests, setExpandedAssigneeNests] = useState<Set<string>>(new Set());
  const [confirmState, setConfirmState] = useState<{ show: boolean; todoId: string | null }>({ show: false, todoId: null });
  const [deleteConfirmState, setDeleteConfirmState] = useState<{ show: boolean; id?: string; ids?: string[] }>({ show: false });

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const handleMarkDoneClick = useCallback((todoId: string) => setConfirmState({ show: true, todoId }), []);
  const handleConfirmMarkDone = () => {
    if (confirmState.todoId) {
      onStatusUpdate(confirmState.todoId, 'Done');
    }
    setConfirmState({ show: false, todoId: null });
  };
  const handleCancelMarkDone = () => setConfirmState({ show: false, todoId: null });

  const handleDeleteClick = useCallback((id: string) => setDeleteConfirmState({ show: true, id }), []);
  const handleDeleteGroupClick = useCallback((ids: string[]) => setDeleteConfirmState({ show: true, ids }), []);
  const handleConfirmDelete = () => {
    if (deleteConfirmState.id) {
      onDelete({ id: deleteConfirmState.id });
    } else if (deleteConfirmState.ids) {
      onDelete({ ids: deleteConfirmState.ids });
      setSelectedIds([]);
      setSelectAll(false);
    }
    setDeleteConfirmState({ show: false });
  };
  const handleCancelDelete = () => setDeleteConfirmState({ show: false });

  const allTaskIdsOnPage = useMemo(() => groups.flatMap((g) => getLeafTasks(g).map((x) => x._id)), [groups]);

  const taskStatusMap = useMemo(() => {
    const map = new Map<string, 'Pending' | 'Done'>();
    groups.forEach((g) => {
      getLeafTasks(g).forEach((t) => {
        map.set(t._id, t.status);
      });
    });
    return map;
  }, [groups]);

  const selectedDoneIds = useMemo(() => selectedIds.filter((id) => taskStatusMap.get(id) === 'Done'), [selectedIds, taskStatusMap]);

  const handleExpandToggle = useCallback((groupKey: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupKey)) {
        next.delete(groupKey);
        setExpandedAssigneeNests((prevNests) => {
          const n = new Set(prevNests);
          [...prevNests].forEach((k) => {
            if (k.startsWith(`${groupKey}::`)) {
              n.delete(k);
            }
          });
          return n;
        });
      } else {
        next.add(groupKey);
      }
      return next;
    });
  }, []);

  const handleAssigneeNestToggle = useCallback((nestKey: string) => {
    setExpandedAssigneeNests((prev) => {
      const next = new Set(prev);
      if (next.has(nestKey)) next.delete(nestKey);
      else next.add(nestKey);
      return next;
    });
  }, []);

  const handleExpandAllTrue = () => {
    setExpandAll(true);
    const gKeys = groups.filter((g) => g.hasExpandData).map((g) => g.groupKey);
    setExpandedGroups(new Set(gKeys));
    const nestKeys = new Set<string>();
    groups.forEach((g) => {
      if (!g.hasExpandData || viewType !== 'all-todo') {
        return;
      }
      getAllTodoExpandSegments(getLeafTasks(g)).forEach((seg) => {
        if (seg.kind === 'assigneeNest') {
          nestKeys.add(`${g.groupKey}::${seg.assigneeKey}`);
        }
      });
    });
    setExpandedAssigneeNests(nestKeys);
  };

  const handleExpandAllFalse = () => {
    setExpandAll(false);
    setExpandedGroups(new Set());
    setExpandedAssigneeNests(new Set());
  };

  const hasExpandableItems = groups.some((g) => g.hasExpandData);

  const handleCheckboxChange = useCallback((todoId: string, checked: boolean) => {
    setSelectedIds((prev) => (checked ? [...prev, todoId] : prev.filter((id) => id !== todoId)));
    if (!checked) {
      setSelectAll(false);
    }
  }, []);

  const handleSelectAll = (checked: boolean) => {
    setSelectAll(checked);
    if (checked) {
      setSelectedIds([...allTaskIdsOnPage]);
    } else {
      setSelectedIds([]);
    }
  };

  const handleBulkAction = (status: 'Pending' | 'Done') => {
    if (selectedIds.length > 0) {
      onBulkStatusUpdate(selectedIds, status);
      setSelectedIds([]);
      setSelectAll(false);
    }
  };

  const toggleRowLeafTaskIds = useCallback((row: IMyTodoListRow, checked: boolean) => {
    const ids = getLeafTasks(row).map((x) => x._id);
    if (checked) {
      setSelectedIds((prev) => Array.from(new Set([...prev, ...ids])));
    } else {
      setSelectedIds((prev) => prev.filter((id) => !ids.includes(id)));
      setSelectAll(false);
    }
  }, []);

  const toggleAssigneeNestTaskIds = useCallback((nestTasks: IMyTodo[], checked: boolean) => {
    const ids = nestTasks.map((x) => x._id);
    if (checked) {
      setSelectedIds((prev) => Array.from(new Set([...prev, ...ids])));
    } else {
      setSelectedIds((prev) => prev.filter((id) => !ids.includes(id)));
      setSelectAll(false);
    }
  }, []);

  const endIndex = Math.min(currentPage * pageSize, totalCount);
  const hasMore = endIndex < totalCount;
  const colSpan = showAssigneeColumn ? 12 : 11;

  const renderRowsForListRow = (row: IMyTodoListRow): React.ReactNode[] => {
    const leafTasks = getLeafTasks(row);
    if (!row.hasExpandData) {
      const singleTodo = row.task ?? (row.expandData?.length === 1 ? row.expandData[0] : undefined);
      if (!singleTodo) {
        return [];
      }
      return [
        <TaskRow
          key={`t-${singleTodo._id}`}
          todo={singleTodo}
          fallbackDevOrder={row.devOrderId}
          indent="none"
          isSelected={selectedSet.has(singleTodo._id)}
          showAssigneeColumn={showAssigneeColumn}
          viewType={viewType}
          onCheckboxChange={handleCheckboxChange}
          onMarkDone={handleMarkDoneClick}
          onDelete={handleDeleteClick}
        />,
      ];
    }
    const groupExpanded = expandedGroups.has(row.groupKey);
    const groupSelected = leafTasks.length > 0 && leafTasks.every((x) => selectedSet.has(x._id));
    const summary = (
      <GroupSummaryRow
        key={`g-${row.groupKey}`}
        row={row}
        expanded={groupExpanded}
        selected={groupSelected}
        showAssigneeColumn={showAssigneeColumn}
        viewType={viewType}
        onExpandToggle={handleExpandToggle}
        onToggleSelect={toggleRowLeafTaskIds}
        onDeleteGroup={handleDeleteGroupClick}
      />
    );
    if (!groupExpanded) {
      return [summary];
    }
    const taskRow = (todo: IMyTodo, keyPrefix: string, indent: 'task' | 'task-nested') => (
      <TaskRow
        key={`${keyPrefix}${todo._id}`}
        todo={todo}
        fallbackDevOrder={row.devOrderId}
        indent={indent}
        isSelected={selectedSet.has(todo._id)}
        showAssigneeColumn={showAssigneeColumn}
        viewType={viewType}
        onCheckboxChange={handleCheckboxChange}
        onMarkDone={handleMarkDoneClick}
        onDelete={handleDeleteClick}
      />
    );
    if (viewType === 'my-todo') {
      return [summary, ...leafTasks.map((todo) => taskRow(todo, 't-', 'task'))];
    }
    const segments = getAllTodoExpandSegments(leafTasks);
    const childRows: React.ReactNode[] = [];
    segments.forEach((seg) => {
      if (seg.kind === 'flat') {
        childRows.push(taskRow(seg.task, 't-', 'task'));
      } else {
        const nestKey = `${row.groupKey}::${seg.assigneeKey}`;
        const nestExpanded = expandedAssigneeNests.has(nestKey);
        const nestSelected = seg.tasks.length > 0 && seg.tasks.every((x) => selectedSet.has(x._id));
        childRows.push(
          <AssigneeNestRow
            key={`nest-${nestKey}`}
            parentRow={row}
            nestTasks={seg.tasks}
            nestKey={nestKey}
            expanded={nestExpanded}
            selected={nestSelected}
            showAssigneeColumn={showAssigneeColumn}
            onToggleExpand={handleAssigneeNestToggle}
            onToggleSelect={toggleAssigneeNestTaskIds}
          />,
        );
        if (nestExpanded) {
          seg.tasks.forEach((todo) => {
            childRows.push(taskRow(todo, 't-nested-', 'task-nested'));
          });
        }
      }
    });
    return [summary, ...childRows];
  };

  const renderTableContent = (): React.ReactNode => {
    if (loading && isEmpty(groups)) {
      return (
        <tr>
          <td colSpan={colSpan} className="text-center py-4">
            <Loader />
          </td>
        </tr>
      );
    }

    if (!loading && isEmpty(groups)) {
      return (
        <tr>
          <td colSpan={colSpan} className="text-center py-3">
            <div className="font-weight-medium text-dark mb-1">{viewType === 'my-todo' ? t('MY_TODO.LIST.NO_TASKS_MY') : t('MY_TODO.LIST.NO_TASKS_ALL')}</div>
          </td>
        </tr>
      );
    }
    return groups.flatMap((row) => renderRowsForListRow(row));
  };

  return (
    <div>
      <div className="table-responsive mb-4 my-todo-table-container" id={SCROLLABLE_DIV_ID}>
        <InfiniteScroll
          dataLength={groups?.length ?? 0}
          hasMore={hasMore}
          next={onLoadMore}
          loader={loading && <Loader />}
          scrollableTarget={SCROLLABLE_DIV_ID}
          className="fixed-col-container"
        >
          <table id="MyTodoTable" className="table common-custom-table mb-0">
            <thead>
              <tr>
                <th className="w-50px w-xs-40px">
                  <div className="common-checkbox margin-auto">
                    <input
                      type="checkbox"
                      id="check_all_todos"
                      checked={selectAll}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      disabled={!allTaskIdsOnPage.length}
                    />
                    <label htmlFor="check_all_todos" className="chklabel" />
                  </div>
                </th>
                <th className="w-50px w-xs-40px">
                  {hasExpandableItems &&
                    (expandAll ? (
                      <button type="button" className="btn-up-down" aria-label={t('MY_TODO.LIST.COLLAPSE_ALL')} onClick={handleExpandAllFalse}>
                        <span className="icon-new-down-arrow-icon icon-inverse" />
                      </button>
                    ) : (
                      <button type="button" className="btn-up-down" aria-label={t('MY_TODO.LIST.EXPAND_ALL')} onClick={handleExpandAllTrue}>
                        <span className="icon-new-down-arrow-icon" />
                      </button>
                    ))}
                </th>
                <th className="w-60px text-left">{t('MY_TODO.LIST.IMAGE')}</th>
                <th className="w-120px">{t('MY_TODO.LIST.PRODUCT_REF')}</th>
                <th className="w-200px">{t('MY_TODO.LIST.TASK')}</th>
                <th>{t('MY_TODO.LIST.ASSIGNED_BY')}</th>
                {showAssigneeColumn && <th>{t('MY_TODO.LIST.ASSIGNED_TO')}</th>}
                <th className="w-100px">{t('MY_TODO.LIST.COMPLETION')}</th>
                <th>{t('MY_TODO.LIST.LOCATION')}</th>
                <th>{t('MY_TODO.LIST.DATE')}</th>
                <th className="w-50px">{t('MY_TODO.LIST.TIME')}</th>
                <th className="w-50px" aria-label={t('MY_TODO.LIST.ACTIONS')} />
              </tr>
            </thead>
            <tbody>{renderTableContent()}</tbody>
          </table>
        </InfiniteScroll>
      </div>

      {viewType === 'my-todo' && (
        <div className="floting-actions">
          <CustomTooltip placement="top" tooltipText={t('MY_TODO.LIST.MARK_AS_DONE')}>
            <button
              type="button"
              disabled={selectedIds.length === 0 || selectedDoneIds.length > 0}
              className="btn"
              aria-label={t('MY_TODO.LIST.MARK_AS_DONE')}
              onClick={() => handleBulkAction('Done')}
            >
              <i className="icon-Complete-task f-28" />
            </button>
          </CustomTooltip>
          <CustomTooltip placement="top" tooltipText={selectedDoneIds.length === 0 ? t('MY_TODO.LIST.DELETE_DISABLED_PENDING') : t('MY_TODO.LIST.DELETE_TASK')}>
            <button
              type="button"
              disabled={selectedDoneIds.length === 0}
              className="btn"
              aria-label={t('MY_TODO.LIST.DELETE_TASK')}
              onClick={() => {
                if (selectedDoneIds.length > 0) setDeleteConfirmState({ show: true, ids: selectedDoneIds });
              }}
            >
              <i className="icon-Delete f-28" />
            </button>
          </CustomTooltip>
        </div>
      )}

      {confirmState.show && (
        <ConfirmationPopup
          onOpen={confirmState.show}
          onConfirm={handleConfirmMarkDone}
          title={t('MY_TODO.LIST.CONFIRM_MARK_DONE_TITLE')}
          message={t('MY_TODO.LIST.CONFIRM_MARK_DONE_MESSAGE')}
          toggle={handleCancelMarkDone}
        />
      )}
      {deleteConfirmState.show && (
        <ConfirmationPopup
          onOpen={deleteConfirmState.show}
          onConfirm={handleConfirmDelete}
          title={deleteConfirmState.ids ? t('MY_TODO.LIST.CONFIRM_DELETE_GROUP_TITLE') : t('MY_TODO.LIST.CONFIRM_DELETE_TITLE')}
          message={deleteConfirmState.ids ? t('MY_TODO.LIST.CONFIRM_DELETE_GROUP_MESSAGE') : t('MY_TODO.LIST.CONFIRM_DELETE_MESSAGE')}
          toggle={handleCancelDelete}
        />
      )}
    </div>
  );
};

export default MyTodoList;
